import {
  SsoConnection,
  SsoConnectionType,
  SsoConnectionUser,
  SsoConnectionUserStatus,
  SsoConnectionUserRole,
  SsoEmailDomain,
  SsoRoleMapping,
  SsoUserRoleTarget,
  SupplierUserRole,
} from '@choco-admin/types';

import type {
  SsoPageConnectionFragmentFragment,
  SsoPageConnectionUserFragmentFragment,
  SsoPageEmailDomainFragmentFragment,
  SsoPageRoleMappingFragmentFragment,
} from '../../../generated';
import type { SsoUserIssue, SsoUserIssueKind, SsoWriteFailure } from '../types';

const CONNECTION_TYPE_LABELS: Record<SsoConnectionType, string> = {
  saml: 'SAML',
  oidc: 'OIDC',
  social: 'Social',
  other: 'Other',
};

const CONNECTION_TYPES: SsoConnectionType[] = [
  'saml',
  'oidc',
  'social',
  'other',
];

const USER_STATUSES: SsoConnectionUserStatus[] = [
  'confirmed',
  'externalProvider',
  'disabled',
  'unknown',
];

/**
 * Codegen adds '%future added value' to every enum, so a newer backend can send
 * a member this build has never heard of. Fall back rather than render it raw.
 */
const toConnectionType = (value: string): SsoConnectionType =>
  CONNECTION_TYPES.find((type) => type === value) ?? 'other';

const toUserStatus = (value: string): SsoConnectionUserStatus =>
  USER_STATUSES.find((status) => status === value) ?? 'unknown';

const SUPPLIER_USER_ROLES: SupplierUserRole[] = [
  SupplierUserRole.ADMIN,
  SupplierUserRole.SALES_REP,
];

/**
 * Returns undefined for a role this build has never heard of, which only
 * happens when the backend gains a member the portal has not been updated for.
 */
const toSupplierUserRole = (value: string): SupplierUserRole | undefined =>
  SUPPLIER_USER_ROLES.find((role) => role === value);

const USER_STATUS_LABELS: Record<SsoConnectionUserStatus, string> = {
  confirmed: 'Confirmed',
  externalProvider: 'Federated',
  disabled: 'Disabled',
  unknown: 'Unknown',
};

export const connectionTypeLabel = (type: SsoConnectionType): string =>
  CONNECTION_TYPE_LABELS[type] ?? 'Other';

export const userStatusLabel = (status: SsoConnectionUserStatus): string =>
  USER_STATUS_LABELS[status] ?? 'Unknown';

export const mapToSsoEmailDomain = (
  emailDomain: SsoPageEmailDomainFragmentFragment
): SsoEmailDomain => ({
  connectionId: emailDomain.connectionId,
  emailDomain: emailDomain.emailDomain,
  exclusiveFederatedLogin: emailDomain.exclusiveFederatedLogin,
  createdAt: emailDomain.createdAt ?? undefined,
  updatedAt: emailDomain.updatedAt ?? undefined,
});

export const mapToSsoConnection = (
  connection: SsoPageConnectionFragmentFragment
): SsoConnection => ({
  id: connection.id,
  name: connection.name,
  type: toConnectionType(connection.type),
  emailDomains: connection.emailDomains,
  emailDomainDetails: connection.emailDomainDetails.map(mapToSsoEmailDomain),
  signOutEnabled: connection.signOutEnabled,
  attributeMappings: connection.attributeMappings.map((mapping) => ({
    cognitoAttribute: mapping.cognitoAttribute,
    idpAttribute: mapping.idpAttribute,
  })),
  createdAt: connection.createdAt ?? undefined,
  updatedAt: connection.updatedAt ?? undefined,
});

export const mapToSsoConnections = (
  connections: SsoPageConnectionFragmentFragment[]
): SsoConnection[] => connections.map(mapToSsoConnection);

const mapToSsoConnectionUserRole = (
  role: SsoPageConnectionUserFragmentFragment['mappedFederatedRoles'][number]
): SsoConnectionUserRole => ({
  federatedRole: role.federatedRole,
  mapping: role.mapping
    ? {
        supplierId: role.mapping.supplier.id,
        chocoSupplierRole: toSupplierUserRole(role.mapping.chocoSupplierRole),
      }
    : undefined,
});

export const mapToSsoConnectionUser = (
  user: SsoPageConnectionUserFragmentFragment
): SsoConnectionUser => ({
  id: user.id,
  chocoUserId: user.chocoUserId ?? undefined,
  email: user.email ?? undefined,
  status: toUserStatus(user.status),
  enabled: user.enabled,
  federatedRoles: user.federatedRoles,
  mappedFederatedRoles: user.mappedFederatedRoles.map((role) =>
    mapToSsoConnectionUserRole(role)
  ),
  mappedAttributes: user.mappedAttributes.map((attribute) => ({
    idpAttribute: attribute.idpAttribute,
    cognitoAttribute: attribute.cognitoAttribute,
    value: attribute.value ?? undefined,
  })),
  createdAt: user.createdAt ?? undefined,
  lastModifiedAt: user.lastModifiedAt ?? undefined,
});

/**
 * Reads one mapped attribute by its Choco-side name. Returns undefined when the
 * connection does not map it, which is normal — which attributes arrive is
 * customer configuration.
 */
export const mappedValue = (
  user: SsoConnectionUser,
  cognitoAttribute: string
): string | undefined =>
  user.mappedAttributes.find(
    (attribute) => attribute.cognitoAttribute === cognitoAttribute
  )?.value;

export const mapToSsoConnectionUsers = (
  users: SsoPageConnectionUserFragmentFragment[]
): SsoConnectionUser[] => users.map(mapToSsoConnectionUser);

export const mapToSsoRoleMapping = (
  roleMapping: SsoPageRoleMappingFragmentFragment
): SsoRoleMapping => ({
  connectionId: roleMapping.connectionId,
  federatedRole: roleMapping.federatedRole,
  supplierId: roleMapping.supplier.id,
  supplierName: roleMapping.supplier.node?.name ?? undefined,
  supplierReferenceId: roleMapping.supplier.node?.referenceId ?? undefined,
  chocoSupplierRole: toSupplierUserRole(roleMapping.chocoSupplierRole),
  createdAt: roleMapping.createdAt ?? undefined,
  updatedAt: roleMapping.updatedAt ?? undefined,
});

export const mapToSsoRoleMappings = (
  roleMappings: SsoPageRoleMappingFragmentFragment[]
): SsoRoleMapping[] => roleMappings.map(mapToSsoRoleMapping);

/**
 * The sentence to show when a write is refused. A validation failure names the
 * offending fields, because its own message says only that something is wrong.
 */
export const failureMessage = (failure: SsoWriteFailure): string => {
  if (failure.kind !== 'validation') {
    return failure.message;
  }

  const reasons = failure.invalidFields.map((field) => field.reason);

  return reasons.length > 0 ? reasons.join(' ') : failure.message;
};

/**
 * The reason the backend gave for one input field, so the form can show it
 * against the control the person has to fix rather than in a banner.
 */
export const reasonForField = (
  failure: SsoWriteFailure,
  fieldName: string
): string | undefined =>
  failure.kind === 'validation'
    ? failure.invalidFields.find((field) => field.fieldName === fieldName)
        ?.reason
    : undefined;

/**
 * The canonical spelling of an email domain, matching the backend's own
 * `normalizeEmailDomain`: trimmed and lower-cased.
 *
 * Both are needed. A stored row is keyed on the normalised domain, but Cognito
 * does not normalise the identifiers it routes, so comparing the two sides on
 * case alone would read a padded Cognito spelling as a domain with no row.
 */
export const normalizeEmailDomain = (emailDomain: string): string =>
  emailDomain.trim().toLowerCase();

/** Enough of a uuid to tell two records apart, matching the profile chips. */
const UUID_PREFIX_LENGTH = 8;

/**
 * A uuid cut to its prefix, with an ellipsis when anything was dropped. Mirrors
 * CopyableIdChip, so a shortened id looks the same wherever it appears.
 */
export const shortenId = (value: string): string =>
  value.length > UUID_PREFIX_LENGTH
    ? `${value.slice(0, UUID_PREFIX_LENGTH)}\u2026`
    : value;

const ISSUE_LABELS: Record<SsoUserIssueKind, string> = {
  noRolesAsserted: 'No role sent',
  noRoleResolves: 'No role mapped',
  conflictingRoles: 'Conflicting roles',
  noChocoAccount: 'No Choco account',
};

export const issueLabel = (kind: SsoUserIssueKind): string =>
  ISSUE_LABELS[kind];

/** Two resolved roles agree only when they grant the same thing. */
const targetKey = (target: SsoUserRoleTarget): string =>
  `${target.supplierId}:${target.chocoSupplierRole ?? 'unknown'}`;

/**
 * The configuration problems a user's row should warn about.
 *
 * Resolution takes the first role that resolves and mappings carry no priority
 * or ordering, so two roles granting different things is a real defect: which
 * one wins is undefined and can change between token refreshes. It is also the
 * one problem that looks completely normal on the row without this check.
 */
export const userConfigIssues = (user: SsoConnectionUser): SsoUserIssue[] => {
  const issues: SsoUserIssue[] = [];
  const targets = user.mappedFederatedRoles
    .map((role) => role.mapping)
    .filter((mapping): mapping is SsoUserRoleTarget => Boolean(mapping));

  if (user.mappedFederatedRoles.length === 0) {
    issues.push({
      kind: 'noRolesAsserted',
      message:
        'This provider sent no role for this person, so they have no distributor access.',
    });
  } else if (targets.length === 0) {
    issues.push({
      kind: 'noRoleResolves',
      message:
        'None of the roles this provider sent is mapped, so this person has no distributor access.',
    });
  } else if (new Set(targets.map(targetKey)).size > 1) {
    issues.push({
      kind: 'conflictingRoles',
      message:
        'This person holds mapped roles that grant different access. Which one applies is not decided, and it can change when they next sign in.',
    });
  }

  if (!user.chocoUserId) {
    issues.push({
      kind: 'noChocoAccount',
      message: 'No Choco account is linked to this person yet.',
    });
  }

  return issues;
};
